import { Alert, Button, Card, Checkbox, Flex, Form, Input, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { useSession } from './session';

const { Title, Text } = Typography;

interface LoginValues {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export function LoginPage(): ReactNode {
  const { t } = useTranslation();
  const signIn = useSession((s) => s.signIn);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values: LoginValues): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await signIn(values.username, values.password, values.rememberMe === true);
    } catch (e) {
      // The API deliberately gives the same answer for a wrong password and an
      // unknown username, so this shows exactly what it said and invents nothing.
      setError(e instanceof ApiError ? e.message : 'Could not sign in. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex align="center" justify="center" style={{ minHeight: '100dvh', padding: 16 }}>
      <Card style={{ width: '100%', maxWidth: 400 }}>
        <Flex vertical gap={20}>
          <Flex vertical gap={2}>
            <Title level={3} style={{ margin: 0 }}>
              {t('app.name')}
            </Title>
            <Text type="secondary">{t('app.subtitle')}</Text>
          </Flex>

          {error !== null && <Alert type="error" message={error} showIcon />}

          <Form<LoginValues>
            layout="vertical"
            onFinish={(values) => {
              void onFinish(values);
            }}
            requiredMark={false}
          >
            <Form.Item
              name="username"
              label={t('auth.username')}
              // No format rule. A username is an identifier, not an email —
              // requiring one meant a technician without a work email could
              // not sign in at all.
              rules={[{ required: true, message: t('auth.enterUsername') }]}
            >
              <Input size="large" autoComplete="username" autoFocus />
            </Form.Item>
            <Form.Item
              name="password"
              label={t('auth.password')}
              rules={[{ required: true, message: 'Enter your password.' }]}
            >
              {/* Input.Password carries the show/hide eye by default. */}
              <Input.Password size="large" autoComplete="current-password" />
            </Form.Item>
            <Form.Item name="rememberMe" valuePropName="checked" initialValue={false}>
              {/* Off by default. Without it the refresh cookie is a SESSION
                  cookie, so a shared showroom tablet does not hand the next
                  advisor the previous one's session. */}
              <Checkbox>{t('auth.rememberMe')}</Checkbox>
            </Form.Item>
            <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
              {submitting ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </Form>
        </Flex>
      </Card>
    </Flex>
  );
}
